/**
 * The event vocabulary. Pure — these functions build payloads and nothing else.
 *
 * Naming lives in the core rather than at each call site so the Worker and the browser
 * emit the same strings by construction. A rename that only half-lands is the failure
 * mode this prevents, and it is invisible until a funnel quietly reads zero.
 *
 * Every event here goes to PostHog, once — see CLAUDE.md, User Events.
 */

/**
 * Every name this page can emit. A runtime list rather than a bare type union, so a
 * test can enumerate it — the study app's page is being retired onto this one, and
 * "did each of its events survive?" is a question that has to be answerable in code.
 */
import type { ArrivedFrom } from './returnTo';

export const EVENT_NAMES = [
  'landing_page_viewed',
  'landing_program_clicked',
  'preview_viewed',
  'preview_program_learn_more_clicked',
  'preview_program_notify_changed',
  'login_opened',
  'tool_opened',
  'notify_form_submitted',
  'notify_signup_confirmed',
  'notify_signup_rejected',
  'notify_signup_failed',
  'notify_signup_recorded',
  'suggestion_box_opened',
  'study_opened',
  'preview_collection_filtered',
  'preview_collection_searched',
  'labs_front_door_answered',
  'labs_stamp_earned',
] as const;

export type EventName = (typeof EVENT_NAMES)[number];

export interface AnalyticsEvent {
  name: EventName;
  properties: Record<string, string | number | boolean | string[]>;
}

/** The headline variant every browser-side funnel event carries, so the experiment is measurable. */
export type Variant = string;

/**
 * Somebody arrived on the page. This is the entry step of the Preview funnel, and the only
 * event every route fires — the study app's header button emits `preview_opened`, but a
 * bookmark, a link or a search emits nothing at all, so anything else read as "arrived"
 * would count those people as a drop-off at step one.
 *
 * `arrived_from` is the route, and it is a PROPERTY rather than a second event because the
 * question ("who came, and how?") is one question. It is recoverable from `$current_url`
 * today, but only by pattern-matching a query string that already varies — `?from=study-guide`,
 * bare, and with a `#` on the end all appear within two days of real traffic. A named
 * property does not rot the way a regex over a URL does.
 *
 * `preview-updates` is a link in the monthly email (see `core/updates.ts`); it is the
 * only way to tell an email-driven visit from a bookmark.
 */
export const landingPageViewed = (variant: Variant, arrivedFrom: ArrivedFrom = 'direct'): AnalyticsEvent => ({
  name: 'landing_page_viewed',
  properties: { experiment_variant: variant, arrived_from: arrivedFrom },
});

export const loginOpened = (variant: Variant): AnalyticsEvent => ({
  name: 'login_opened',
  properties: { experiment_variant: variant },
});

/** `source` is set only by the front door, so every other open keeps its old shape. */
export const toolOpened = (tool: string, source?: 'front_door' | 'stamp_card'): AnalyticsEvent => ({
  name: 'tool_opened',
  properties: source ? { tool, source } : { tool },
});

/**
 * The header's Study button was clicked: the way back to the study app. Named to
 * mirror the study app's `preview_opened`, which is the same header, the other way.
 * `from_study` distinguishes a return visit from someone arriving another way.
 */
export const studyOpened = (source: string, fromStudy = false): AnalyticsEvent => ({
  name: 'study_opened',
  properties: { source, from_study: fromStudy },
});

/** Where a "Learn more" link sat on the page, as the study app named the two places. */
export type LearnMorePlacement = 'featured' | 'card';

export interface PreviewViewedDetails {
  /** Every program on the page, in display order. */
  programIds: string[];
  /** The ones shown full-width with a pitch. */
  featuredProgramIds: string[];
}

/**
 * The two events below are the study app's, copied name-for-name from
 * `sgapp-client/src/pages/preview/analytics.ts` so the funnel survives that page's
 * retirement. `preview_viewed` is the `preview-button` experiment's primary metric —
 * a renamed counterpart would read as zero rather than as a continuation, which is the
 * failure CLAUDE.md's User Events rule describes.
 */
export const previewViewed = (details: PreviewViewedDetails): AnalyticsEvent => ({
  name: 'preview_viewed',
  properties: {
    program_ids: details.programIds,
    featured_program_ids: details.featuredProgramIds,
    program_count: details.programIds.length,
  },
});

export const previewLearnMoreClicked = (
  programId: string,
  placement: LearnMorePlacement,
): AnalyticsEvent => ({
  name: 'preview_program_learn_more_clicked',
  properties: { program_id: programId, placement },
});

export const notifyFormSubmitted = (variant: Variant): AnalyticsEvent => ({
  name: 'notify_form_submitted',
  properties: { experiment_variant: variant },
});

export const notifySignupConfirmed = (variant: Variant): AnalyticsEvent => ({
  name: 'notify_signup_confirmed',
  properties: { experiment_variant: variant },
});

export const notifySignupRejected = (reason: string): AnalyticsEvent => ({
  name: 'notify_signup_rejected',
  properties: { reason },
});

export const notifySignupFailed = (status: number): AnalyticsEvent => ({
  name: 'notify_signup_failed',
  properties: { status },
});

/**
 * The suggestion button was clicked.
 *
 * PostHog records the rest of the box itself — `survey shown` when the form opens,
 * `survey sent` when it is submitted. This is the step before both, and the only one
 * this app can see: a click that never became a `survey shown` means the form failed to
 * open, and a click that never became a `survey sent` means the teacher opened the box
 * and had nothing they wanted to say. Neither is visible from PostHog's events alone.
 */
export const suggestionBoxOpened = (surface: string): AnalyticsEvent => ({
  name: 'suggestion_box_opened',
  properties: { surface },
});

/**
 * Server-side only. `was_duplicate` is what the funnel needs to tell a new teacher from
 * a returning one, and it is exactly what the response body must never reveal — the page
 * would otherwise become an address-enumeration oracle.
 */
export const notifySignupRecorded = (wasDuplicate: boolean): AnalyticsEvent => ({
  name: 'notify_signup_recorded',
  properties: { was_duplicate: wasDuplicate },
});

/** Collection discovery events deliberately exclude free-text searches. */
export const previewCollectionFiltered = (need: string): AnalyticsEvent => ({
  name: 'preview_collection_filtered',
  properties: { need },
});

export const previewCollectionSearched = (need: string, resultCount: number): AnalyticsEvent => ({
  name: 'preview_collection_searched',
  properties: { need, result_count: resultCount },
});

/**
 * A visitor answered the front door (E13). `answer` is the question they picked, or
 * `other` with the question they typed in `text`. Unlike a collection search, the typed
 * question is a submission the visitor chose to send us, and reading it is the point.
 * Picking a tool still sends its own `tool_opened`, so E10's metric needs no change.
 */
export const frontDoorAnswered = (
  answer: string,
  details: { layout: string; tool?: string; text?: string },
): AnalyticsEvent => ({
  name: 'labs_front_door_answered',
  properties: {
    answer,
    layout: details.layout,
    ...(details.tool ? { tool: details.tool } : {}),
    ...(details.text ? { text: details.text } : {}),
  },
});

/**
 * A visitor was shown a stamp filled for the first time on this device (E17). `tool` is
 * set for a tool stamp; the bonus stamps have none. Sent only from the `stamps` arm.
 */
export const stampEarned = (stamp: string, tool?: string): AnalyticsEvent => ({
  name: 'labs_stamp_earned',
  properties: { stamp, ...(tool ? { tool } : {}) },
});
